import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { ALEA_INFO, RCP_OPTIONS, RCP_DEFAUT, CULTURES, getSemisPourCulture } from '../../shared/constants';
import { api } from '../../shared/api/api';
import { libelleHorizon } from '../../shared/utils/horizon';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import CarteRisquePRA from '../../shared/charts/CarteRisquePRA';
import LegendeCarte from '../../shared/charts/LegendeCarte';

// Seulement PRA/RA/Département pour cette page (pas de Région), à la
// demande d'Aliénor.
const ECHELLES = [
  { value: 'pra', label: 'Petite région agricole' },
  { value: 'ra', label: 'Région agricole' },
  { value: 'departement', label: 'Département' },
];

/**
 * Comparer / Fréquence des aléas (nombres aléas) : 3 cartes (2020/2035/2050),
 * teinte selon l'aléa choisi dans la sidebar — jours/an avec cet aléa.
 * ⚠️ Nouvelle carte, mock (pas de route confirmée).
 */
export default function ComparerFrequenceAleas() {
  const { idExploit } = useOAD();
  const [alea, setAlea] = React.useState('CDC');
  const [rcp, setRcp] = React.useState(RCP_DEFAUT);
  const [echelle, setEchelle] = React.useState(ECHELLES[0].value);
  const [culture, setCulture] = React.useState(CULTURES[0].id);
  const semisDisponibles = getSemisPourCulture(culture);
  const [semis, setSemis] = React.useState(semisDisponibles[0]?.value);

  React.useEffect(() => {
    if (!semisDisponibles.some((s) => s.value === semis)) {
      setSemis(semisDisponibles[0]?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culture]);

  const parametres = React.useMemo(
    () => ({ idExploitation: idExploit, alea, rcp, echelle, idProduction: culture, semis }),
    [idExploit, alea, rcp, echelle, culture, semis]
  );

  const { data, loading, error } = useApiData(api.getCartesFrequenceAleas, parametres);
  // Object.keys() ne respecte pas l'ordre d'insertion pour les clés qui
  // ressemblent à des entiers ("2" passe toujours avant "reference", quel
  // que soit l'ordre réel) : on fige l'ordre explicitement plutôt que de
  // faire confiance à Object.keys. C'est exactement ce qui donnait
  // "+2, Référence, +2.7" au lieu de "Référence, +2, +2.7".
  const ORDRE_HORIZONS = { classique: [2020, 2035, 2050], tracc: ['reference', '2', '2.7'] };
  const horizons = data?.cartes
    ? (rcp === 'tracc' ? ORDRE_HORIZONS.tracc : ORDRE_HORIZONS.classique).filter((h) => data.cartes[h])
    : [];

  const { min, max } = React.useMemo(() => {
    if (!data?.cartes) return { min: 0, max: 0 };
    const valeurs = horizons.flatMap(
      (h) => data.cartes[h]?.data?.features?.map((f) => Number(f.properties.joursAlea)) ?? []
    );
    return valeurs.length ? { min: Math.min(...valeurs), max: Math.max(...valeurs) } : { min: 0, max: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="comparer-comprendre"
        niveau2="comparer"
        niveau3="frequence-aleas"
        idExploit={idExploit}
      >
        <label className="sidebar-champ">
          <span className="sidebar-label">Culture</span>
          <select value={culture} onChange={(e) => setCulture(Number(e.target.value))}>
            {CULTURES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Date de semis</span>
          <select value={semis ?? ''} onChange={(e) => setSemis(e.target.value)}>
            {semisDisponibles.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Aléa</span>
          <select value={alea} onChange={(e) => setAlea(e.target.value)}>
            {Object.keys(ALEA_INFO).map((code) => (
              <option key={code} value={code}>{ALEA_INFO[code].label}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Géographie</span>
          <select value={echelle} onChange={(e) => setEchelle(e.target.value)}>
            {ECHELLES.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </label>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-comparer-aleas"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">Fréquence de l'aléa « {ALEA_INFO[alea].label} »</h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading && <p className="tdb-chargement">Chargement…</p>}

          {data?.cartes && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {horizons.map((horizon) => (
                  <div key={horizon} style={{ flex: '1 1 260px', minWidth: 240 }}>
                    <h3 className="ig-camembert-titre">{libelleHorizon(rcp, horizon)}</h3>
                    <LimiteErreur cle={`aleas-carte-${alea}-${horizon}`}>
                      <CarteRisquePRA
                        data={data.cartes[horizon].data}
                        pointExploitation={data.pointExploitation}
                        champ="joursAlea"
                        continu={{ type: 'mono', base: ALEA_INFO[alea].color, min, max }}
                        unite="j/an"
                        afficherLegende={false}
                      />
                    </LimiteErreur>
                  </div>
                ))}
              </div>
              <LegendeCarte continu={{ type: 'mono', base: ALEA_INFO[alea].color, min, max }} min={min} max={max} unite="j/an" />
            </>
          )}
        </section>
      </main>
    </div>
  );
}
